import { useQuery } from "@apollo/client/react";
import {
  ContentBreadcrumbFragment_context,
  GetMultipleChoiceData,
  GetMultipleChoiceDataVariables,
  MultipleChoiceType,
} from "__generated__/types";
import { CenteredSimpleLayout } from "common/components/layouts/CenteredSimpleLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { Loading } from "common/components/Loading";
import { NoDataError } from "common/components/NoDataError";
import { SimpleTabs } from "common/components/tabs/SimpleTabs";
import { SubText } from "common/components/Typography";
import { getSyllabus } from "common/hooks/useBreadcrumbs";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { urls } from "common/urls";
import { isSyllabusForStudent } from "domain/common/utils";
import { GroupingQuestion } from "domain/multiple-choice/GroupingQuestion";
import { MultipleChoice } from "domain/multiple-choice/MultipleChoice";
import { MULTIPLE_CHOICE_DATA_QUERY } from "domain/pages/MultipleChoicePage";
import React from "react";
import { Link } from "react-router-dom";
import styles from "./MultipleChoiceContainer.module.css";

interface Props {
  multipleChoiceId: string;
  tabs?: Array<{
    title: string;
    content: React.ReactNode;
  }>;
  containerType:
    | ContentBreadcrumbFragment_context["__typename"]
    | "SelfAssessment"
    | undefined;
  onFinishedElement?: () => void;
}

export const QuestionContainer: React.FC<React.PropsWithChildren<Props>> = ({
  multipleChoiceId,
  tabs,
  onFinishedElement,
  containerType,
  children,
}) => {
  const { myExamSyllabi } = useMyExamSyllabus();
  const { data, loading } = useQuery<
    GetMultipleChoiceData,
    GetMultipleChoiceDataVariables
  >(MULTIPLE_CHOICE_DATA_QUERY, {
    variables: {
      multipleChoiceId: multipleChoiceId,
    },
  });

  if (loading) {
    return (
      <CenteredSimpleLayout>
        <Loading />
      </CenteredSimpleLayout>
    );
  }

  if (!data || !data.multipleChoiceById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const isSyllabus = isSyllabusForStudent(
    myExamSyllabi,
    getSyllabus(data.multipleChoiceById.context),
  );

  return (
    <section className={styles.container}>
      {data.multipleChoiceById.type == MultipleChoiceType.STANDARD ? (
        <MultipleChoice
          key={data.multipleChoiceById.id}
          multipleChoice={data.multipleChoiceById}
          onFinishedElement={onFinishedElement}
          isSyllabus={
            isSyllabus &&
            data.multipleChoiceById.canCreateProgression.canPerform
          }
          containerType={containerType}
        />
      ) : (
        <GroupingQuestion
          key={data.multipleChoiceById.id}
          multipleChoice={data.multipleChoiceById}
          onFinishedElement={onFinishedElement}
          isSyllabus={
            isSyllabus &&
            data.multipleChoiceById.canCreateProgression.canPerform
          }
          containerType={containerType}
        />
      )}
      {!!tabs?.length && <SimpleTabs content={tabs} />}
      {children}
      {data.multipleChoiceById.canUpdateDetails.canPerform && (
        <SubText>
          <Link to={urls.assignmentAdminEdit(data.multipleChoiceById.id)}>
            Rediger oppgavedetaljer
          </Link>
          <br />
          <Link to={urls.assignmentAdmin(data.multipleChoiceById.id)}>
            {data.multipleChoiceById.type == MultipleChoiceType.STANDARD
              ? "Rediger spørsmål"
              : "Rediger kategorier"}
          </Link>
        </SubText>
      )}
    </section>
  );
};
